<script setup>
import { ElMessage, ElMessageBox } from "element-plus";
import { ref, reactive, watch, onMounted, getCurrentInstance } from "vue";
import EffluentStandard from "./effluentStandard.vue";
import FlowTree from "./tree.vue";
import Editor from "./editor.vue";
import ImageUpload from "./imageUpload.vue";
import StationCard from "./stationCard.vue";

import {
  queryStationInfoByPlantId,
  queryEffluentStandard,
  inertStationInfo,
  updateStationInfo,
  insertOrUpdateStationInfo,
  queryFlowChart,
  insertFlowChart,
  updateFlowChart,
} from "@/api/stationBaseInfo";

import { queryDictData } from "@/api/common";

import { deleteFlowChart } from "@/api/chart";

const props = defineProps({
  params: {
    type: Object,
    required: false,
    default: null,
  },
});

const { proxy } = getCurrentInstance();

// 表单ref
const ruleFormRef = ref(null);

// 厂区树选项
const plantsOptions = ref(undefined);

const flowChartRef = ref(null);

// ID卡ref
const stationCardRef = ref(null);

// 出水标准弹窗ref
const effluentStandardRef = ref(null);

// 厂站类型 供水还是净水（1供水厂，2净水厂）
const newPlantType = ref("2");

//是否接入巨控 默认否
const radioJk = ref("0");

// 判断当前厂站是否已经填写了信息
const isFillInfo = ref(false);

// 进水标准
const inletType = ref(null);

// 表单对象
const formData = ref({
  id: undefined,
  deptId: undefined,
  name: undefined,
  plantsStatus: undefined,
  processingTechnology: undefined,
  plantsTypeCascader: [],
  keyPoint: undefined,
  pumpStationNumber: undefined,
  operatingScale: undefined,
  agreementSize: undefined,
  processingPrice: undefined,
  waterStandard: undefined,
  coversArea: undefined,
  inletType: undefined,
  lng: undefined,
  lat: undefined,
  juKongFlag: "0",
  address: undefined,
  plantsIntroduction: "<p></p>",
  promotionalImage: undefined,
  publicWelfareImage: undefined,
  plantsType: undefined,
  plantsTypeDetail: undefined,
  operateType: undefined,
  finalStandard: {
    id: undefined,
    type: undefined,
    deptId: undefined,
  },
  ietStandStandard: {
    id: undefined,
    type: undefined,
    deptId: undefined,
  },
});

// 自定义表单校验规则
const validatePublicImg = (rule, value, callback) => {
  if (formData.value.promotionalImage) {
    callback();
  }
  callback(new Error("厂站宣传图不能为空"));
};

// 表单校验
const rules = reactive({
  name: [{ required: true, message: "不能为空", trigger: "blur" }],
  plantsStatus: [{ required: true, message: "不能为空", trigger: "change" }],
  keyPoint: [{ required: true, message: "不能为空", trigger: "change" }],
  pumpStationNumber: [
    { required: true, message: "泵站数量不能为空", trigger: "blur" },
    { pattern: /^[0-9]+$/, message: "泵站数量必须为整数", trigger: "blur" },
  ],
  operatingScale: [{ required: true, message: "不能为空", trigger: "blur" }],
  agreementSize: [{ required: true, message: "不能为空", trigger: "blur" }],
  processingPrice: [{ required: true, message: "不能为空", trigger: "blur" }],
  waterStandard: [{ required: true, message: "不能为空", trigger: "blur" }],
  coversArea: [{ required: true, message: "不能为空", trigger: "blur" }],
  lng: [{ required: true, message: "不能为空", trigger: "blur" }],
  lat: [{ required: true, message: "不能为空", trigger: "blur" }],
  plantsTypeCascader: [
    {
      type: "array",
      required: true,
      validator: (rule, value, callback) => {
        let cascader = formData.value.plantsTypeCascader;
        if (cascader) {
          cascader = cascader.filter(Boolean);
        }

        if (!cascader || !cascader.length) {
          callback(new Error("不能为空"));
          return;
        }

        callback();
      },
      trigger: "change",
    },
  ],
  juKongFlag: [{ required: true, message: "是否接入巨控", trigger: "blur" }],
  address: [{ required: true, message: "不能为空", trigger: "blur" }],
  promotionalImage: [
    { required: true, validator: validatePublicImg, trigger: "change" },
  ],
});

//运行状态
const plantStatus = ref([
  {
    value: "2",
    label: "已商运",
  },
  {
    value: "1",
    label: "建设中",
  },
]);

const plantsTypeOptions = ref([
  {
    value: 1,
    label: "委托运营",
    children: [
      {
        value: 1,
        label: "供水厂",
        children: [
          {
            value: 1,
            label: "城市",
          },
          {
            value: 2,
            label: "乡镇",
          },
        ],
      },
      {
        value: 2,
        label: "净水厂",
        children: [
          {
            value: 1,
            label: "城市",
          },
          {
            value: 2,
            label: "乡镇",
          },
          {
            value: 3,
            label: "工业",
          },
        ],
      },
    ],
  },
  {
    value: 2,
    label: "国润自营",
    children: [
      {
        value: 1,
        label: "供水厂",
        children: [
          {
            value: 1,
            label: "城市",
          },
          {
            value: 2,
            label: "乡镇",
          },
        ],
      },
      {
        value: 2,
        label: "净水厂",
        children: [
          {
            value: 1,
            label: "城市",
          },
          {
            value: 2,
            label: "乡镇",
          },
          {
            value: 3,
            label: "工业",
          },
        ],
      },
    ],
  },
]);

//是否重点排污单位
const plantImport = ref([
  {
    value: "1",
    label: "是",
  },
  {
    value: "0",
    label: "否",
  },
]);
//净水出水标准字典数据
const outPurOptions = ref([]);

//供水出水标准字典数据
const outSupOptions = ref([]);

//进水类型字典数据
const inOptions = ref([]);

//宣传图
const imgBtnShow = ref(true); //图片按钮显示
const advertImgUrlList = ref([]);
const otherPhotoImgObj = ref([]);
const otherImgUrlList = ref([]);
const advertPhotoImgObj = ref([]);

// 预览
const previewPlant = () => {
  stationCardRef.value?.show(props.params.id);
};

const selectChange = (val, name) => {
  formData[name] = val;
};

// 进水标准变化
const inletTypeChange = (val) => {
  formData.value.inletType = val ? val.join(",") : "";
};

const changeCas = (val) => {
  newPlantType.value = val[1];
  getWaterStand(val[1]);
  // 变化后清空类型和标准
  inletType.value = null;
  formData.value.inletType = "";
  formData.value.waterStandard = "";
  // 将数组拆散为三个单独的属性
  formData.value.operateType = val[0];
  formData.value.plantsType = val[1];
  formData.value.plantsTypeDetail = val[2];
};

const getWaterStand = (s) => {
  if (s == "2") {
    resetPurForm();
    getPurStandData("edit");
  } else {
    resetSupForm();
    getSupStandData("edit");
  }
};

const resetPurForm = () => {};

const resetSupForm = () => {};

// 净水标准
const getPurStandData = (s) => {
  if (!formData.value.waterStandard) {
    return false;
  }
};

// 供水标准
const getSupStandData = (s) => {};

const addFlowChartRow = (row = {}) => {
  insertFlowChart({
    deptId: props.params.id,
    parentId: "",
    type: "2",
    label1: "",
  }).then(() => {
    getFlowChartList();
  });
};

const editFlowChartRow = (row) => {
  updateFlowChart({
    deptId: props.params.id,
    id: row.id,
    lable1: row.lable1,
    type: "2",
  }).then((res) => {
    getFlowChartList();
  });
};

const delFlowChartRow = (row) => {
  ElMessageBox.confirm("是否确认删除数据项?", "警告", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "warning",
  })
    .then(() => {
      deleteFlowChart(row.id).then(() => {
        getFlowChartList();
        ElMessage.success("删除成功");
      });
    })
    .catch(() => {
      flowChartRef.value.disabled = false;
    });
};

// 进水标准确定回调
const effluentSubmit = (data) => {
  formData.value.opStandardWaterInletReq = { ...data };
};

// 点击进水标准
const openInStand = () => {
  effluentStandardRef.value?.handleDialogOpen(
    props.params.id,
    formData.value.waterStandard
  );
};

//宣传图回调方法
const promotionalImageChange = (file) => {
  formData.value.promotionalImage = file.reduceUrl;
};

//主要工艺段图片回调方法
const publicWelfareImageChange = (file) => {
  formData.value.publicWelfareImage = file.reduceUrl;
};

/** 保存按钮 */
const submitBaseForm = () => {
  ruleFormRef.value?.validate((valid) => {
    if (valid) {
      //
      insertOrUpdateStationInfo(formData.value).then((res) => {
        if (res.code == "2000") {
          ElMessage.success("操作成功");
        }
      });
    }
  });
};

// 重置表单
const resetForm = () => {
  formData.value = {
    name: undefined,
    plantsStatus: undefined,
    processingTechnology: undefined,
    plantsTypeCascader: [],
    keyPoint: undefined,
    pumpStationNumber: undefined,
    operatingScale: undefined,
    agreementSize: undefined,
    processingPrice: undefined,
    waterStandard: undefined,
    coversArea: undefined,
    inletType: undefined,
    lng: undefined,
    lat: undefined,
    juKongFlag: "0",
    address: undefined,
    plantsIntroduction: "<p></p>",
    plantsType: undefined,
    plantsTypeDetail: undefined,
    operateType: undefined,
    promotionalImage: undefined,
    publicWelfareImage: undefined,
    finalStandard: {
      id: undefined,
      type: undefined,
      deptId: undefined,
    },
    ietStandStandard: {
      id: undefined,
      type: undefined,
      deptId: undefined,
    },
  };

  advertPhotoImgObj.value = [];
  advertImgUrlList.value = [];
  otherImgUrlList.value = [];
  otherPhotoImgObj.value = [];
};

// 获取厂站信息
const getStationInfo = () => {
  queryStationInfoByPlantId(formData.value.deptId)
    .then((res) => {
      if (JSON.stringify(res.data) != "{}") {
        isFillInfo.value = true;
      } else {
        isFillInfo.value = false;
        resetForm();
        formData.value.name = props.params.name;
        formData.value.deptId = props.params.id;
        ruleFormRef.value?.resetFields();
      }
      if (isFillInfo.value) {
        for (const key in formData.value) {
          formData.value[key] = res.data[key];
        }
        formData.value.waterStandard = res.data.waterStandard * 1;
        inletType.value = formData.value.inletType
          ? formData.value.inletType.split(",")
          : null;

        // 将拆散的厂站类型组合成数组传给级联选择器组件
        if (res.data.plantsTypeDetail) {
          formData.value.plantsTypeCascader = [
            res.data.operateType * 1,
            res.data.plantsType * 1,
            res.data.plantsTypeDetail * 1,
          ];
        }
      }
    })
    .finally(() => {
      getEffluentStandard();
      getInWaterType();
    });
};

// 获取净水出水标准
const getEffluentStandard = () => {
  queryDictData("sys_outwater_stand").then((res) => {
    outPurOptions.value = res.data || [];
  });
};

// 获取进水类型
const getInWaterType = () => {
  queryDictData("sys_inwater_type").then((res) => {
    inOptions.value = res.data || [];
  });
};

// 获取工艺流程图
const getFlowChartList = () => {
  queryFlowChart(props.params.id).then((res) => {
    flowChartRef.value.show(res.data);
  });
};

watch(
  () => props.params,
  (newVal) => {
    if (newVal.id && ["供水厂", "净水厂"].includes(newVal.type)) {
      formData.value.name = newVal.name;
      formData.value.deptId = newVal.id;
      getStationInfo();
      getFlowChartList();
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

defineExpose({
  resetForm,
  getStationInfo,
});
</script>

<template>
  <div>
    <el-row type="flex" justify="end">
      <el-col>
        <el-button
          type="primary"
          plain
          icon="View"
          style="float: right"
          size="small"
          @click="previewPlant()"
          >预览</el-button
        >
      </el-col>
    </el-row>
    <div class="my_line_box">
      <el-divider direction="vertical" class="my_line"></el-divider>
      <span class="my_line_txt">基本信息</span>
    </div>
    <el-form
      ref="ruleFormRef"
      :model="formData"
      :rules="rules"
      label-width="120px"
      label-position="top"
    >
      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="厂站名称" prop="name">
            <el-input
              v-model="formData.name"
              placeholder="请输入厂站名称"
              disabled
            />
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="运行状态" prop="plantsStatus">
            <el-select
              v-model="formData.plantsStatus"
              placeholder="请选择运行状态"
              style="width: 100%"
              @change="
                (val) => {
                  selectChange(val, 'plantsStatus');
                }
              "
            >
              <el-option
                v-for="item in plantStatus"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="主体处理工艺" prop="processingTechnology">
            <el-input
              v-model="formData.processingTechnology"
              placeholder="请输入主体处理工艺"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="厂站类型" prop="plantsTypeCascader">
            <el-cascader
              v-model="formData.plantsTypeCascader"
              :options="plantsTypeOptions"
              style="width: 100%"
              @change="changeCas"
            />
          </el-form-item>
        </el-col>

        <el-col :span="8" v-if="newPlantType == '2'">
          <el-form-item label="是否重点排污单位" prop="keyPoint">
            <el-select
              v-model="formData.keyPoint"
              name="keyPoint"
              style="width: 100%"
              placeholder="请选择是否重点排污单位"
              @change="
                (val) => {
                  selectChange(val, 'keyPoint');
                }
              "
            >
              <el-option
                v-for="item in plantImport"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              ></el-option>
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="8" v-if="newPlantType == '2'">
          <el-form-item label="泵站数量" prop="pumpStationNumber">
            <el-input
              v-model="formData.pumpStationNumber"
              placeholder="请输入泵站数量"
            >
              <template #append>个</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="已运营规模" prop="operatingScale">
            <el-input
              v-model.trim="formData.operatingScale"
              placeholder="请输入已运营规模"
            >
              <template #append>吨/日</template>
            </el-input>
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="协议规模" prop="agreementSize">
            <el-input
              v-model.trim="formData.agreementSize"
              placeholder="请输入协议规模"
            >
              <template #append>吨/日</template>
            </el-input>
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="水处理单价">
            <el-input
              v-model.trim="formData.processingPrice"
              placeholder="请输入水处理单价"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="出水标准" prop="waterStandard">
            <el-select
              v-model="formData.waterStandard"
              style="width: 100%"
              placeholder="请选择出水标准"
              v-if="formData.plantsTypeCascader[1] == 2"
              @change="getWaterStand('2')"
              :disabled="!formData.plantsTypeCascader[1]"
            >
              <el-option
                v-for="dict in outPurOptions"
                :key="dict.dictValue"
                :label="dict.dictLabel"
                :value="dict.dictValue"
              />
            </el-select>
            <el-select
              v-model="formData.waterStandard"
              placeholder="请选择出水标准"
              @change="getWaterStand('1')"
              style="width: 100%"
              v-else
              :disabled="!formData.plantsTypeCascader[1]"
            >
              <el-option
                v-for="dict in outSupOptions"
                :key="dict.dictValue"
                :label="dict.dictLabel"
                :value="dict.dictValue"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="进水标准" required>
            <el-button
              type="info"
              plain
              @click="openInStand"
              style="width: 100%; text-align: left"
              >点击填写进水标准</el-button
            >
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="占地面积" prop="coversArea">
            <el-input
              v-model.trim="formData.coversArea"
              placeholder="请输入占地面积"
              name="coversArea"
            >
              <template #append>亩</template>
            </el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="进水类型" prop="inletType">
            <el-select
              v-model="inletType"
              style="width: 100%"
              multiple
              placeholder="请选择进水类型"
              @change="inletTypeChange"
            >
              <el-option
                v-for="dict in inOptions"
                :key="dict.dictValue"
                :label="dict.dictLabel"
                :value="dict.dictValue"
              />
            </el-select>
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="厂站经度" prop="lng">
            <el-input
              v-model.trim="formData.lng"
              placeholder="请输入厂站经度"
            />
          </el-form-item>
        </el-col>

        <el-col :span="8">
          <el-form-item label="厂站纬度" prop="lat">
            <el-input
              v-model.trim="formData.lat"
              placeholder="请输入厂站纬度"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="8">
          <el-form-item label="是否接入巨控" prop="juKongFlag">
            <el-radio-group v-model="formData.juKongFlag">
              <el-radio v-model="radioJk" label="1" name="juKongFlag"
                >是</el-radio
              >
              <el-radio v-model="radioJk" label="0" name="juKongFlag"
                >否</el-radio
              >
            </el-radio-group>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col>
          <el-form-item label="厂站地址" prop="address">
            <el-input
              v-model.trim="formData.address"
              placeholder="请输入厂站地址"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col>
          <el-form-item label="厂站简介" prop="plantsIntroduction">
            <editor
              ref="myQuillEditor"
              v-model:content="formData.plantsIntroduction"
              :min-height="180"
              style="width: 500px"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col :span="5">
          <el-form-item label="厂站宣传图" prop="promotionalImage">
            <imageUpload
              :url="formData.promotionalImage"
              @fileUpload="promotionalImageChange"
            />
          </el-form-item>
        </el-col>
        <el-col :span="19">
          <el-form-item label="主要工艺段图片" prop="publicWelfareImage">
            <imageUpload
              :url="formData.publicWelfareImage"
              @fileUpload="publicWelfareImageChange"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="10">
        <el-col>
          <el-form-item class="rightBtn">
            <el-button size="small" type="primary" @click="submitBaseForm"
              >保 存</el-button
            >
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <div>
      <div class="my_line_box">
        <el-divider direction="vertical" class="my_line"></el-divider>
        <span class="my_line_txt">工艺流程</span>
      </div>
      <flow-tree
        ref="flowChartRef"
        @addRow="addFlowChartRow"
        @editRow="editFlowChartRow"
        @delRow="delFlowChartRow"
      />
    </div>
    <effluent-standard
      ref="effluentStandardRef"
      @afterSubmit="effluentSubmit"
    />

    <station-card ref="stationCardRef" />
  </div>
</template>

<style scoped>
.my_line_box {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
.my_line {
  background-color: #3f78f7;
  width: 4px;
  height: 14px;
}
.my_line_txt {
  font-family: "PingFang SC";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-align: justify;
  line-height: 14px;
}

.rightBtn {
  display: flex;
  justify-content: end;
}
</style>
